// utils/financialCalculations.js
// Advanced Financial Calculations for Dashboard Analytics

/**
 * Calculate average monthly values from historical data
 * @param {Array} transactions - Array of transactions
 * @param {number} months - Number of months to average (default: 3)
 * @returns {Object} - { avgIncome, avgExpenses, avgSavings }
 */
export const calculateAverages = (transactions, months = 3) => {
  const now = new Date();
  const monthsAgo = new Date(now.getFullYear(), now.getMonth() - months, 1);
  
  const recentTransactions = transactions.filter(t => new Date(t.date) >= monthsAgo);
  
  const totalIncome = recentTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);
    
  const totalExpenses = recentTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  
  return {
    avgIncome: totalIncome / months,
    avgExpenses: totalExpenses / months,
    avgSavings: (totalIncome - totalExpenses) / months
  };
};

/**
 * Generate cash flow forecast for future months
 * @param {number} currentBalance - Current account balance
 * @param {Array} transactions - Historical transactions
 * @param {number} monthsAhead - Number of months to forecast
 * @returns {Array} - Forecast data with confidence bands
 */
export const generateCashFlowForecast = (currentBalance, transactions, monthsAhead = 3) => {
  const { avgIncome, avgExpenses, avgSavings } = calculateAverages(transactions, 3);
  
  // Calculate variance for confidence bands
  const variance = avgExpenses * 0.1; // ±10% variance
  
  const forecast = [];
  let projectedBalance = currentBalance;
  
  const now = new Date();
  
  for (let i = 0; i <= monthsAhead; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthName = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    
    if (i === 0) {
      // Current month (actual data)
      forecast.push({
        month: monthName,
        balance: currentBalance,
        type: 'actual',
        upperBound: currentBalance,
        lowerBound: currentBalance
      });
    } else {
      // Future months (projected)
      projectedBalance += avgSavings;
      forecast.push({
        month: monthName,
        balance: projectedBalance,
        type: 'projected',
        upperBound: projectedBalance + variance * i,
        lowerBound: projectedBalance - variance * i
      });
    }
  }
  
  return forecast;
};

/**
 * Calculate burn rate (daily spending rate)
 * @param {Array} transactions - Current month's transactions
 * @returns {Object} - { dailyBurnRate, monthlyProjection }
 */
export const calculateBurnRate = (transactions) => {
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const currentDay = now.getDate();
  
  const monthExpenses = transactions
    .filter(t => {
      const tDate = new Date(t.date);
      return t.type === 'expense' && 
             tDate.getMonth() === now.getMonth() && 
             tDate.getFullYear() === now.getFullYear();
    })
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  
  const dailyBurnRate = monthExpenses / currentDay;
  const monthlyProjection = dailyBurnRate * daysInMonth;
  
  return {
    dailyBurnRate,
    monthlyProjection,
    daysElapsed: currentDay,
    daysRemaining: daysInMonth - currentDay
  };
};

/**
 * Calculate runway (months until balance reaches zero)
 * @param {number} currentBalance - Current balance
 * @param {number} dailyBurnRate - Daily spending rate
 * @param {number} avgIncome - Average monthly income
 * @returns {Object} - { months, days, totalDays, status }
 */
export const calculateRunway = (currentBalance, dailyBurnRate, avgIncome) => {
  const monthlyNetBurn = (dailyBurnRate * 30) - avgIncome;
  
  if (monthlyNetBurn <= 0) {
    // Positive cash flow - infinite runway
    return {
      months: Infinity,
      days: Infinity,
      totalDays: Infinity,
      status: 'positive'
    };
  }
  
  const totalDays = Math.floor(currentBalance / (monthlyNetBurn / 30));
  const months = Math.floor(totalDays / 30);
  const days = totalDays % 30;
  
  let status = 'healthy';
  if (months < 3) status = 'critical';
  else if (months < 6) status = 'warning';
  
  return {
    months,
    days,
    totalDays,
    status
  };
};

/**
 * Detect anomalies in transactions (outliers)
 * @param {Array} transactions - All transactions
 * @returns {Array} - Anomalous transactions with deviation info
 */
export const detectAnomalies = (transactions) => {
  const anomalies = [];
  
  // Group by category
  const byCategory = transactions.reduce((acc, t) => {
    const cat = t.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});
  
  Object.entries(byCategory).forEach(([category, categoryTransactions]) => {
    if (categoryTransactions.length < 3) return; // Need at least 3 data points
    
    const amounts = categoryTransactions.map(t => Number(t.amount || 0));
    const mean = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    const variance = amounts.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / amounts.length;
    const stdDev = Math.sqrt(variance);
    
    categoryTransactions.forEach(transaction => {
      const amount = Number(transaction.amount || 0);
      const deviation = (amount - mean) / (stdDev || 1);
      
      // Flag if more than 2 standard deviations away
      if (Math.abs(deviation) > 2) {
        anomalies.push({
          ...transaction,
          category,
          mean,
          deviation: deviation.toFixed(2),
          percentageAboveAverage: ((amount - mean) / mean * 100).toFixed(1),
          severity: Math.abs(deviation) > 3 ? 'high' : 'medium'
        });
      }
    });
  });
  
  return anomalies.sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation));
};

/**
 * Detect recurring transaction patterns
 * @param {Array} transactions - All transactions
 * @returns {Array} - Detected recurring patterns
 */
export const detectRecurringPatterns = (transactions) => {
  const patterns = [];
  
  // Group by similar amount and description
  const grouped = transactions.reduce((acc, t) => {
    const key = `${t.description}-${Math.round(t.amount)}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  
  Object.entries(grouped).forEach(([key, group]) => {
    if (group.length < 2) return;
    
    // Sort by date
    const sorted = group.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Calculate day intervals between transactions
    const intervals = [];
    for (let i = 1; i < sorted.length; i++) {
      const days = Math.round((new Date(sorted[i].date) - new Date(sorted[i-1].date)) / (1000 * 60 * 60 * 24));
      intervals.push(days);
    }
    
    // Check if intervals are consistent (monthly ~30 days, weekly ~7 days)
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const variance = intervals.reduce((sum, val) => sum + Math.pow(val - avgInterval, 2), 0) / intervals.length;
    const stdDev = Math.sqrt(variance);
    
    // Low variance indicates recurring pattern
    if (stdDev < avgInterval * 0.2) { // 20% tolerance
      let frequency = 'unknown';
      let confidence = 'medium';
      
      if (Math.abs(avgInterval - 30) < 5) {
        frequency = 'monthly';
        confidence = stdDev < 2 ? 'high' : 'medium';
      } else if (Math.abs(avgInterval - 7) < 2) {
        frequency = 'weekly';
        confidence = stdDev < 1 ? 'high' : 'medium';
      } else if (Math.abs(avgInterval - 14) < 3) {
        frequency = 'biweekly';
        confidence = 'medium';
      } else if (Math.abs(avgInterval - 90) < 10) {
        frequency = 'quarterly';
        confidence = 'medium';
      }
      
      if (frequency !== 'unknown') {
        patterns.push({
          description: sorted[0].description,
          amount: sorted[0].amount,
          category: sorted[0].category,
          frequency,
          confidence,
          occurrences: sorted.length,
          avgInterval: Math.round(avgInterval),
          lastDate: sorted[sorted.length - 1].date,
          nextExpectedDate: new Date(new Date(sorted[sorted.length - 1].date).getTime() + avgInterval * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
      }
    }
  });
  
  return patterns.sort((a, b) => {
    const confidenceOrder = { high: 3, medium: 2, low: 1 };
    return (confidenceOrder[b.confidence] || 0) - (confidenceOrder[a.confidence] || 0);
  });
};

/**
 * Calculate financial health score (0-100)
 * @param {Object} metrics - Financial metrics
 * @returns {Object} - { score, breakdown, grade, recommendations }
 */
export const calculateFinancialHealthScore = (metrics) => {
  const {
    totalIncome = 0,
    totalExpenses = 0,
    monthlyBudget = 0,
    currentBalance = 0,
    categoryData = [],
    transactions = []
  } = metrics;
  
  let score = 0;
  const breakdown = {};
  
  // 1. Budget Adherence (30 points)
  if (categoryData.length > 0) {
    const categoriesUnderBudget = categoryData.filter(c => c.budgetUtilization <= 100).length;
    const budgetAdherence = (categoriesUnderBudget / categoryData.length) * 100;
    breakdown.budgetAdherence = {
      score: Math.round(budgetAdherence * 0.3),
      percentage: budgetAdherence.toFixed(1),
      weight: 30
    };
    score += breakdown.budgetAdherence.score;
  } else {
    breakdown.budgetAdherence = { score: 15, percentage: 50, weight: 30 }; // Neutral
    score += 15;
  }
  
  // 2. Savings Rate (25 points)
  const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0;
  let savingsScore = 0;
  if (savingsRate >= 20) savingsScore = 25;
  else if (savingsRate >= 10) savingsScore = 20;
  else if (savingsRate >= 5) savingsScore = 15;
  else if (savingsRate >= 0) savingsScore = 10;
  else savingsScore = 0; // Negative savings
  
  breakdown.savingsRate = {
    score: savingsScore,
    percentage: savingsRate.toFixed(1),
    weight: 25
  };
  score += savingsScore;
  
  // 3. Emergency Fund (20 points) - Assuming 3 months expenses is ideal
  const monthsOfExpenses = totalExpenses > 0 ? currentBalance / totalExpenses : 0;
  let emergencyScore = 0;
  if (monthsOfExpenses >= 6) emergencyScore = 20;
  else if (monthsOfExpenses >= 3) emergencyScore = 15;
  else if (monthsOfExpenses >= 1) emergencyScore = 10;
  else if (monthsOfExpenses >= 0.5) emergencyScore = 5;
  
  breakdown.emergencyFund = {
    score: emergencyScore,
    months: monthsOfExpenses.toFixed(1),
    weight: 20
  };
  score += emergencyScore;
  
  // 4. Spending Consistency (15 points) - Lower variance is better
  if (transactions.length >= 30) {
    const dailySpending = [];
    const groupedByDay = transactions.reduce((acc, t) => {
      if (t.type === 'expense') {
        const day = new Date(t.date).toISOString().split('T')[0];
        if (!acc[day]) acc[day] = 0;
        acc[day] += Number(t.amount || 0);
      }
      return acc;
    }, {});
    
    const amounts = Object.values(groupedByDay);
    const mean = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    const variance = amounts.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / amounts.length;
    const coefficientOfVariation = Math.sqrt(variance) / mean;
    
    let consistencyScore = 0;
    if (coefficientOfVariation < 0.5) consistencyScore = 15;
    else if (coefficientOfVariation < 1) consistencyScore = 10;
    else if (coefficientOfVariation < 1.5) consistencyScore = 5;
    
    breakdown.consistency = {
      score: consistencyScore,
      variance: coefficientOfVariation.toFixed(2),
      weight: 15
    };
    score += consistencyScore;
  } else {
    breakdown.consistency = { score: 7, variance: 'N/A', weight: 15 }; // Neutral
    score += 7;
  }
  
  // 5. Income Stability (10 points)
  const incomeTransactions = transactions.filter(t => t.type === 'income');
  let incomeScore = 0;
  if (incomeTransactions.length >= 3) {
    incomeScore = 10;
  } else if (incomeTransactions.length >= 1) {
    incomeScore = 5;
  }
  
  breakdown.incomeStability = {
    score: incomeScore,
    transactions: incomeTransactions.length,
    weight: 10
  };
  score += incomeScore;
  
  // Determine grade
  let grade = 'F';
  if (score >= 90) grade = 'A+';
  else if (score >= 85) grade = 'A';
  else if (score >= 80) grade = 'B+';
  else if (score >= 75) grade = 'B';
  else if (score >= 70) grade = 'C+';
  else if (score >= 65) grade = 'C';
  else if (score >= 60) grade = 'D';
  
  // Generate recommendations
  const recommendations = [];
  if (breakdown.budgetAdherence.score < 15) {
    recommendations.push('Focus on staying within budget limits for each category');
  }
  if (breakdown.savingsRate.score < 15) {
    recommendations.push('Aim to save at least 10-20% of your income');
  }
  if (breakdown.emergencyFund.score < 10) {
    recommendations.push('Build an emergency fund covering 3-6 months of expenses');
  }
  if (breakdown.consistency.score < 7) {
    recommendations.push('Try to maintain more consistent spending patterns');
  }
  
  return {
    score: Math.round(score),
    grade,
    breakdown,
    recommendations
  };
};

/**
 * Generate spending heatmap data (day of week analysis)
 * @param {Array} transactions - All transactions
 * @returns {Array} - Heatmap data by day of week
 */
export const generateSpendingHeatmap = (transactions) => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Initialize data structure
  const heatmapData = dayNames.map(day => ({
    day,
    total: 0,
    count: 0,
    average: 0
  }));
  
  // Aggregate spending by day of week
  transactions
    .filter(t => t.type === 'expense')
    .forEach(t => {
      const dayIndex = new Date(t.date).getDay();
      heatmapData[dayIndex].total += Number(t.amount || 0);
      heatmapData[dayIndex].count += 1;
    });
  
  // Calculate averages
  heatmapData.forEach(day => {
    day.average = day.count > 0 ? day.total / day.count : 0;
  });
  
  return heatmapData;
};

/**
 * Calculate category spending velocity (rate of spend per day)
 * @param {Array} transactions - Current month transactions
 * @param {Array} categories - Category list
 * @returns {Array} - Categories with velocity metrics
 */
export const calculateCategoryVelocity = (transactions, categories) => {
  const now = new Date();
  const currentDay = now.getDate();
  
  return categories.map(category => {
    const categoryExpenses = transactions
      .filter(t => 
        t.type === 'expense' && 
        t.category === category.name &&
        new Date(t.date).getMonth() === now.getMonth()
      )
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);
    
    const dailyRate = categoryExpenses / currentDay;
    const categoryBudget = Number(category.budget || 0);
    const daysUntilOverBudget = categoryBudget > categoryExpenses 
      ? (categoryBudget - categoryExpenses) / dailyRate 
      : 0;
    
    return {
      name: category.name,
      color: category.color,
      spent: categoryExpenses,
      budget: categoryBudget,
      dailyRate: dailyRate,
      daysUntilOverBudget: Math.floor(daysUntilOverBudget),
      status: daysUntilOverBudget < 5 && daysUntilOverBudget > 0 ? 'warning' : 'ok'
    };
  }).filter(c => c.dailyRate > 0);
};
